/**
 * The shop's own invoices from SmartSpaza.
 *
 * Read-only, and there is no "pay now" button, because there is no gateway —
 * §10.2 is explicit that payments are recorded by SmartSpaza when the money is
 * seen to arrive. A button that opened a payment page that did not exist would
 * be worse than the bank details, so what the detail panel shows is what to pay
 * and against which reference.
 *
 * Overdue is surfaced in the row rather than only in a filter. Somebody who has
 * opened this screen has usually opened it because of one particular invoice.
 */

import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import {
  Card,
  EmptyState,
  ErrorState,
  Pill,
  SkeletonRows,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  type PillTone,
} from "@/components/ui";
import { formatMoney } from "@/lib/format";
import {
  billingRepository,
  type InvoiceStatus,
} from "@/repositories/billingRepository";

const TONES: Record<InvoiceStatus, PillTone> = {
  ISSUED: "info",
  PARTIALLY_PAID: "warn",
  PAID: "success",
  OVERDUE: "danger",
  VOID: "neutral",
};

const LABELS: Record<InvoiceStatus, string> = {
  ISSUED: "Issued",
  PARTIALLY_PAID: "Part paid",
  PAID: "Paid",
  OVERDUE: "Overdue",
  VOID: "Cancelled",
};

export function MyInvoices() {
  const [open, setOpen] = useState<string | null>(null);

  const invoices = useQuery({
    queryKey: ["billing", "invoices"],
    queryFn: () => billingRepository.invoices(),
  });

  const detail = useQuery({
    queryKey: ["billing", "invoice", open],
    queryFn: () => billingRepository.invoice(open!),
    enabled: open !== null,
  });

  return (
    <div className="space-y-5">
      <Card flush className="p-5">
        {invoices.isError ? (
          <ErrorState
            title="Invoices could not load"
            onRetry={() => invoices.refetch()}
          />
        ) : invoices.isLoading ? (
          <SkeletonRows rows={6} />
        ) : invoices.data && invoices.data.results.length > 0 ? (
          <Table>
            <THead>
              <TH>Invoice</TH>
              <TH hideBelow="lg">Period</TH>
              <TH hideBelow="md">Due</TH>
              <TH hideBelow="sm" className="text-right">
                Total
              </TH>
              <TH className="text-right">Outstanding</TH>
              <TH>Status</TH>
            </THead>
            <TBody>
              {invoices.data.results.map((row) => (
                <TR
                  key={row.id}
                  onClick={() => setOpen(row.id === open ? null : row.id)}
                >
                  <TD className="font-mono text-xs font-medium text-brand-600">
                    {row.number}
                  </TD>
                  <TD hideBelow="lg" className="text-sm text-ink-500">
                    {row.period_start && row.period_end
                      ? `${row.period_start} to ${row.period_end}`
                      : "—"}
                  </TD>
                  <TD hideBelow="md" className="text-sm text-ink-500">
                    {row.due_date ?? "—"}
                  </TD>
                  <TD hideBelow="sm" className="text-right tabular-nums">
                    {formatMoney(row.total)}
                  </TD>
                  <TD
                    className={
                      Number(row.balance) > 0
                        ? "text-right font-medium tabular-nums text-danger-700"
                        : "text-right tabular-nums text-ink-500"
                    }
                  >
                    {formatMoney(row.balance)}
                  </TD>
                  <TD>
                    <Pill tone={TONES[row.status]}>{LABELS[row.status]}</Pill>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        ) : (
          <EmptyState
            title="No invoices yet"
            description="They appear here as SmartSpaza bills each period."
          />
        )}
      </Card>

      {open && detail.data && <InvoiceDetail invoice={detail.data} />}
    </div>
  );
}

function InvoiceDetail({
  invoice,
}: {
  invoice: NonNullable<Awaited<ReturnType<typeof billingRepository.invoice>>>;
}) {
  return (
    <Card>
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="font-mono text-sm font-semibold text-ink-900">
          {invoice.number}
        </h2>
        <Pill tone={TONES[invoice.status]}>{LABELS[invoice.status]}</Pill>
      </div>

      <Table className="mt-4">
        <THead>
          <TH>Description</TH>
          <TH hideBelow="sm" className="text-right">
            Quantity
          </TH>
          <TH hideBelow="md" className="text-right">
            Price
          </TH>
          <TH className="text-right">Total</TH>
        </THead>
        <TBody>
          {invoice.items.map((item) => (
            <TR key={item.id}>
              <TD className="text-sm text-ink-900">{item.description}</TD>
              <TD
                hideBelow="sm"
                className="text-right tabular-nums text-ink-500"
              >
                {item.quantity}
              </TD>
              <TD
                hideBelow="md"
                className="text-right tabular-nums text-ink-500"
              >
                {formatMoney(item.unit_price)}
              </TD>
              <TD className="text-right tabular-nums">
                {formatMoney(item.total)}
              </TD>
            </TR>
          ))}
        </TBody>
      </Table>

      <dl className="mt-4 ml-auto w-full max-w-xs space-y-1.5 text-sm">
        <Line label="Subtotal" value={invoice.subtotal} />
        {Number(invoice.discount_amount) > 0 && (
          <Line label="Discount" value={`-${invoice.discount_amount}`} />
        )}
        {/* Shown only when there is tax to show. A zero VAT line on an invoice
            from a business that is not VAT registered is a question nobody
            needs to be prompted to ask. */}
        {Number(invoice.tax_amount) > 0 && (
          <Line
            label={`VAT (${invoice.tax_rate_snapshot}%)`}
            value={invoice.tax_amount}
          />
        )}
        <Line label="Total" value={invoice.total} strong />
        <Line label="Paid" value={invoice.amount_paid} />
        <Line label="Outstanding" value={invoice.balance} strong />
      </dl>

      {invoice.notes && (
        <p className="mt-4 text-sm text-ink-600">{invoice.notes}</p>
      )}
    </Card>
  );
}

function Line({
  label,
  value,
  strong,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  return (
    <div className="flex justify-between gap-4">
      <dt className={strong ? "font-medium text-ink-900" : "text-ink-500"}>
        {label}
      </dt>
      <dd
        className={
          strong
            ? "font-medium tabular-nums text-ink-900"
            : "tabular-nums text-ink-600"
        }
      >
        {formatMoney(value)}
      </dd>
    </div>
  );
}
